// Types and interfaces
interface INoImageOptions {
    baseURL?: string | boolean;
}

type ImageType = "avatar" | "default";

// Constants
const DEFAULT_IMAGES = {
    avatar: "/Images/no-image/avatar.jpg",
    default: "/Images/no-image/no-image.png"
} as const;

/**
 * Resolves the image URL based on the provided URL and options.
 * @param url - The original image URL.
 * @param options - Additional options for URL resolution.
 * @returns The resolved image URL.
 */
export function resolveImageUrl(url: string | null | undefined, options?: INoImageOptions | ImageType): string {
    // Handle cases where no URL is provided
    if (!url) {
        return getDefaultImage(options);
    }

    // Apply base URL if specified
    if (typeof options === "object" && options.baseURL) {
        return applyBaseUrl(url, options.baseURL);
    }

    return url;
}

/**
 * Gets the default image based on the provided options.
 * @param options - The options specifying the image type.
 * @returns The URL of the default image.
 */
function getDefaultImage(options?: INoImageOptions | ImageType): string {
    if (options === "avatar") {
        return DEFAULT_IMAGES.avatar;
    }
    return DEFAULT_IMAGES.default;
}

/**
 * Applies the base URL to the given image URL.
 * @param url - The original image URL.
 * @param baseURL - The base URL to be applied.
 * @returns The full image URL with the base URL applied.
 */
function applyBaseUrl(url: string, baseURL: string | boolean): string {
    if (typeof baseURL === "boolean" && baseURL) {
        return `${useRuntimeConfig().public.bucketUrl}${url}`;
    }
    if (typeof baseURL === "string") {
        return `${baseURL}${url}`;
    }
    return url;
}

export default resolveImageUrl;
